import { useReducer, useEffect } from 'react';
import {
  Box,
  Text,
  HStack,
  Select,
  Flex,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { uniqBy } from 'lodash';
import { useRecoilValue } from 'recoil';
import { selectedLockYearState } from '@transport-insights/uikit';

const initialState = {
  corridor: '',
  regionalCouncil: '',
  districtCouncil: '',
  noc: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setCorridor':
      return {
        ...state,
        corridor: action.corridor,
      };
    case 'setRegionalCouncil':
      return {
        ...state,
        regionalCouncil: action.regionalCouncil,
      };
    case 'setDistrictCouncil':
      return {
        ...state,
        districtCouncil: action.districtCouncil,
      };
    case 'setNoc':
      return {
        ...state,
        noc: action.noc,
      };
    case 'setActiveHnoFilter':
      return {
        corridor: action.activeHnoFilter.corridor ?? '',
        regionalCouncil: action.activeHnoFilter.regionalCouncil ?? '',
        districtCouncil: action.activeHnoFilter.districtCouncil ?? '',
        noc: action.activeHnoFilter.noc ?? '',
      };
    case 'reset':
      return {
        ...initialState,
      };
    default:
      throw new Error();
  }
};

export default function HnoFilter({
  selectedHno, data = [], isLoading, activeHnoFilter = null, buttonLabel, ...rest
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Get the selected lock year from Recoil state and determine the label for NOC/IDC based on the year.
  const lockYear = useRecoilValue(selectedLockYearState);
  const isLockYearLessThan2025 = parseInt(lockYear.toString().substring(0, 4), 10) < 2025;
  const nocLabel = isLockYearLessThan2025 ? 'Contract Area (NOC)' : 'Contract Area (IDC)';

  // Filter the data based on the selected filters in the state.
  const filteredCorridorData = data.filter(
    (x) => ((state.regionalCouncil === '' || x.road_region === state.regionalCouncil)
    && (state.districtCouncil === '' || x.road_council === state.districtCouncil)
    && (state.noc === '' || x.noc === state.noc)),
  );
  const filteredRegionalData = data.filter(
    (x) => ((state.corridor === '' || x.corridor === state.corridor)
    && (state.districtCouncil === '' || x.road_council === state.districtCouncil)
    && (state.noc === '' || x.noc === state.noc)),
  );
  const filteredDistrictData = data.filter(
    (x) => ((state.corridor === '' || x.corridor === state.corridor)
    && (state.regionalCouncil === '' || x.road_region === state.regionalCouncil)
    && (state.noc === '' || x.noc === state.noc)),
  );
  const filteredNocData = data.filter(
    (x) => ((state.corridor === '' || x.corridor === state.corridor)
    && (state.regionalCouncil === '' || x.road_region === state.regionalCouncil)
    && (state.districtCouncil === '' || x.road_council === state.districtCouncil)),
  );

  // Create unique lists of corridors, regions, districts, and NOCs based on the filtered data.
  const corridors = uniqBy(filteredCorridorData, 'corridorName')
    .map((x) => ({ id: x.corridor, name: x.corridorName }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const regions = uniqBy(filteredRegionalData, 'regionName')
    .map((x) => ({ id: x.road_region, name: x.regionName }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const districts = uniqBy(filteredDistrictData, 'TLAName')
    .map((x) => ({ id: x.road_council, name: x.TLAName }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const nocs = uniqBy(filteredNocData, 'noc')
    .map((x) => ({ id: x.noc, name: x.noc }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const isSelectedNocAvailable = nocs?.some((x) => x.id === state.noc);

  useEffect(() => {
    if (activeHnoFilter !== null) {
      dispatch({ type: 'setActiveHnoFilter', activeHnoFilter });
    }
  }, [activeHnoFilter]);

  const getHnoNames = () => {
    let filterName = '';
    const regionName = data.find((x) => x.road_region === state.regionalCouncil)?.regionName || null;
    const tlaName = data.find((x) => x.road_council === state.districtCouncil)?.TLAName || null;
    const noc = data.find((x) => x.noc === state.noc)?.noc || null;
    const corridorName = data.find((x) => x.corridor === state.corridor)?.corridorName || null;

    if (regionName) {
      filterName += `State Highways: ${regionName}; `;
    }

    if (tlaName) {
      filterName += `${tlaName}; `;
    }

    if (noc) {
      filterName += `${noc}; `;
    }

    if (corridorName) {
      filterName += `${corridorName}; `;
    }

    if (filterName === '') {
      filterName = 'State Highways';
    } else {
      filterName = filterName.slice(0, -2);
    }

    return {
      filterName, regionName, tlaName, corridorName, noc,
    };
  };

  const handleApply = () => {
    const {
      filterName, regionName, tlaName, corridorName, noc,
    } = getHnoNames();
    selectedHno({
      ...state, regionName, tlaName, corridorName, filterName, noc,
    });
  };

  const handleReset = () => {
    dispatch({ type: 'reset' });
    selectedHno(undefined);
  };

  // Reset NOC state if the selected NOC is not available for the selected lock year.
  useEffect(() => {
    if (!isSelectedNocAvailable && state.noc !== '') {
      handleReset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectedNocAvailable, state.noc]);

  if (isLoading) return <Spinner />;

  return (
    <HStack spacing={4} {...rest}>
      <Box>
        <Text mb={1} fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold">Corridor</Text>
        <Select
          onChange={(e) => dispatch({ type: 'setCorridor', corridor: e.target.value })}
          value={state.corridor}
          size="sm"
        >
          <option value="">All</option>
          {corridors.map((corridor) => (<option key={`corridor_${corridor.id}`} value={corridor.id}>{corridor.name}</option>))}
        </Select>
      </Box>
      <Box>
        <Text mb={1} fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold">Region</Text>
        <Select
          onChange={(e) => dispatch({ type: 'setRegionalCouncil', regionalCouncil: e.target.value })}
          value={state.regionalCouncil}
          size="sm"
        >
          <option value="">All</option>
          {regions.map((region) => (<option key={`region_${region.id}`} value={region.id}>{region.name}</option>))}
        </Select>
      </Box>
      <Box>
        <Text mb={1} fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold">Territorial Authority</Text>
        <Select
          onChange={(e) => dispatch({ type: 'setDistrictCouncil', districtCouncil: e.target.value })}
          value={state.districtCouncil}
          size="sm"
        >
          <option value="">All</option>
          {districts.map((district) => (<option key={`district_${district.id}`} value={district.id}>{district.name}</option>))}
        </Select>
      </Box>
      <Box>
        <Text mb={1} fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold">{nocLabel}</Text>
        <Select
          onChange={(e) => dispatch({ type: 'setNoc', noc: e.target.value })}
          value={state.noc}
          size="sm"
        >
          <option value="">All</option>
          {nocs.map((noc) => (<option key={`noc_${noc.id}`} value={noc.id}>{noc.name}</option>))}
        </Select>
      </Box>
      <Flex alignSelf="flex-end">
        <Button onClick={handleApply} size="sm">{buttonLabel}</Button>
        <Button
          ml={2}
          variant="outline"
          colorScheme="gray"
          size="sm"
          onClick={() => handleReset()}
        >
          Reset
        </Button>
      </Flex>
    </HStack>
  );
}
